import React, { useCallback, useEffect, useState } from 'react';

import Modal from './Modal';
import Button from './Button';
import useSpecialCharHistory from '../../hooks/useSpecialCharHistory';
import { COMMON_SPECIAL_CHARS, formatAltCode } from '../../utils/specialChars';

// Insert-special-character dialog (charmap/Word "Symbol" style): click a
// symbol in the grid to select/highlight it (no side effect yet), then
// "Copy" inserts the selected character via `onInsert(char)` and closes;
// "Cancel" closes without inserting anything. Agnostic of *where* the
// character goes — callers decide how `onInsert` applies it (append, insert
// at cursor, etc).
//
// Prefer the useSpecialCharPicker() hook below for imperative call sites —
// mirrors useConfirm()'s pattern in ConfirmDialog.js.
export function SpecialCharDialog({ isOpen, onClose, onInsert }) {
  const { history, recordUsed } = useSpecialCharHistory();
  const [selected, setSelected] = useState(null);

  // Clear the selection each time the dialog opens, so a stale pick from a
  // previous use doesn't carry over.
  useEffect(() => {
    if (isOpen) setSelected(null);
  }, [isOpen]);

  const handleCopy = () => {
    if (!selected) return;
    onInsert?.(selected.char);
    recordUsed(selected);
    onClose?.();
  };

  const renderCell = (entry) => (
    <button
      key={entry.code}
      type="button"
      className={`ss-special-chars__cell${selected?.code === entry.code ? ' is-selected' : ''}`}
      title={`${entry.name} (${formatAltCode(entry.code)})`}
      onClick={() => setSelected(entry)}
    >
      <span className="ss-special-chars__glyph">{entry.char}</span>
    </button>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Insert Special Character">
      {history.length > 0 && (
        <div className="ss-special-chars__section">
          <h3 className="ss-special-chars__section-title">Recently used</h3>
          <div className="ss-special-chars__grid">{history.map(renderCell)}</div>
        </div>
      )}

      <div className="ss-special-chars__section">
        <h3 className="ss-special-chars__section-title">Common characters</h3>
        <div className="ss-special-chars__grid">{COMMON_SPECIAL_CHARS.map(renderCell)}</div>
      </div>

      <div className="ss-form__actions">
        <Button onClick={handleCopy} disabled={!selected}>
          Copy
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}

// Imperative wiring for a single text field:
//
//   const { openPicker, dialog } = useSpecialCharPicker();
//   ...
//   <button onClick={() => openPicker((char) => insertAtCursor(char))}>Ω</button>
//   {dialog}
//
// `onInsert` is called once per character picked (the dialog stays open for
// multiple picks); it does not close the dialog itself.
export function useSpecialCharPicker() {
  const [state, setState] = useState({ open: false, onInsert: null });

  const openPicker = useCallback((onInsert) => {
    setState({ open: true, onInsert });
  }, []);

  const close = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
  }, []);

  const dialog = <SpecialCharDialog isOpen={state.open} onClose={close} onInsert={state.onInsert} />;

  return { openPicker, dialog };
}

export default SpecialCharDialog;
